import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogMain,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ncoder/ui';
import { grantableRolesFor } from './roleOptions';
import { DialogActions, FormField, ORG_ADMIN_INPUT_CLASS } from './OrgAdminForm';
import type { CreateOrgInvitationInput, ManagementLevel, OrgRole } from '@/services/org-admin';

// Anchor below the trigger (Radix's default "item-aligned" mode opens in an
// unpredictable direction/position) and scroll instead of overflowing once
// the list is taller than the viewport allows.
const SELECT_CONTENT_PROPS = {
  position: 'popper' as const,
  sideOffset: 4,
  className: 'max-h-60 w-[--radix-select-trigger-width] overflow-y-auto',
};

interface InviteFormValues {
  email: string;
  role_id: string;
}

export interface InviteMemberModalProps {
  open: boolean;
  roles: OrgRole[];
  currentUserRole: ManagementLevel | null;
  isSubmitting: boolean;
  onSubmit: (data: CreateOrgInvitationInput) => void;
  onClose: () => void;
}

export function InviteMemberModal({
  open,
  roles,
  currentUserRole,
  isSubmitting,
  onSubmit,
  onClose,
}: InviteMemberModalProps) {
  const roleOptions = grantableRolesFor(currentUserRole, roles);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<InviteFormValues>({
    defaultValues: { email: '', role_id: roleOptions[0]?.id ?? '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!open) return;
    reset({ email: '', role_id: roleOptions[0]?.id ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  const submit = handleSubmit((data) => {
    onSubmit({ email: data.email, role_id: data.role_id });
  });

  // An org with no grantable role is a real dead end — sending an invitation requires one. Say so,
  // instead of rendering an empty dropdown above a permanently disabled button.
  const hasNoGrantableRole = roleOptions.length === 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Invite member</DialogTitle>
          <DialogDescription className="text-[13px]">
            They receive an email with a join link. The role you pick here takes effect the moment
            they accept.
          </DialogDescription>
        </DialogHeader>

        {/* A real form element: Enter in the email field now submits, and the browser applies its
            own email validation before ours. */}
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col gap-5" noValidate>
          <DialogMain className="space-y-4">
            <FormField label="Email address" required error={errors.email?.message}>
              {(fieldProps) => (
                <input
                  {...fieldProps}
                  type="email"
                  autoComplete="off"
                  // The first field of a freshly opened dialog is where the user is going anyway.
                  autoFocus
                  placeholder="name@company.com"
                  disabled={isSubmitting}
                  className={ORG_ADMIN_INPUT_CLASS}
                  {...register('email', {
                    required: 'Enter the email address to invite',
                    pattern: { value: /\S+@\S+\.\S+/, message: 'That does not look like an email address' },
                  })}
                />
              )}
            </FormField>

            <FormField
              label="Role"
              required
              hint={
                hasNoGrantableRole
                  ? 'You have no roles you can grant. Create one on the Roles tab first.'
                  : 'Determines which services they can use once they join.'
              }
            >
              {({ id, ...fieldProps }) => (
                <Controller
                  name="role_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isSubmitting || hasNoGrantableRole}
                    >
                      <SelectTrigger id={id} {...fieldProps} className="h-9 text-[13px]">
                        <SelectValue placeholder="Select a role…" />
                      </SelectTrigger>
                      <SelectContent {...SELECT_CONTENT_PROPS}>
                        {roleOptions.map((role) => (
                          <SelectItem key={role.id} value={role.id} className="text-[13px]">
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              )}
            </FormField>
          </DialogMain>

          <DialogFooter>
            <DialogActions
              onCancel={onClose}
              submitLabel="Send invitation"
              pendingLabel="Sending…"
              isSubmitting={isSubmitting}
              isDisabled={!isValid || hasNoGrantableRole}
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
